import React, { useEffect, useState } from 'react';
import { Avatar, Button, Dropdown, Navbar as FlowbiteNavbar, Tooltip } from 'flowbite-react';
import { Img } from 'react-image';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { InterviewPathType, PaymentStatus, PaymentType, UserExperienceLevelType, UserRole } from 'types';
import {
	checkDisplayUserEmail,
	isUserDriveAdmin,
	isUserJobseeker,
	isUserMentor,
	isUserRecruiter,
	stringAvatar,
} from 'shared-utils';
import { FiExternalLink } from 'react-icons/fi';
import { HiOutlineLogout, HiOutlineUser } from 'react-icons/hi';
import {
	PROFILE_MENU_CLASS,
	PROFILE_MENU_DANGER_ITEM_CLASS,
	PROFILE_MENU_HEADER_CLASS,
	PROFILE_MENU_ITEM_CLASS,
} from './profileMenuStyles';
import { useUserContext } from 'src/context/UserProvider';
import { useMutation } from 'react-query';
import { signout } from 'src/apis/auth';
import { RECRUITER_APP_ENDPOINT, STUDENT_APP_ENDPOINT, WEBSITE_URL, isRecruiterSide } from 'src/config/env.config';
import { useGetMyOpenApplicationsCount } from 'src/hooks/application.hooks';
import { useGetMyRegisteredDrives } from 'src/hooks/offlineDrive.hooks';
import UpdateAvailabilityModal from '../mentor/UpdateAvailabilityModal';
import { sendTrackingEvent } from 'src/utils/gtm';
import { useGetPrepePlanById } from 'src/hooks/prepPlan.hooks';
import { useThemeContext } from 'src/context/ThemeProvider';
import { useLayoutContext } from 'src/context/LayoutProvider';
import { MdAdd } from 'react-icons/md';
import { useRecommendationsContext } from 'src/context/RecommendationsProvider';
import useDisclosure from 'src/hooks/utils/useDisclosure';
import LinkedInModal from '../LinkedInModal';
import ThemeToggleButton from './ThemeToggleButton';
import CollegeSelect from './CollegeSelect';
import { switchCollege } from 'src/apis/user';
import { useToast } from 'src/context/ToastProvider';
import TrialPeriod from './TrialPeriod';
import OfflineDriveDropdown from '../driveAdmin/offlineDriveCandidatesList/OfflineDriveDropdown';

const Navbar = () => {
	const toast = useToast();
	const { themeMode } = useThemeContext();
	const location = useLocation();
	const navigate = useNavigate();
	const { onDeleteUserModalOpen } = useLayoutContext();
	const { isOpen, onClose } = useDisclosure();
	const {
		user,
		isJobseekerLoggedIn,
		isRecruiterLoggedIn,
		isMentorLoggedIn,
		isDriveAdminLoggedIn,
		isAccountManagerLoggedIn,
		isLeadGenLoggedIn,
		isUserUniversityManagerLoggedIn,
		selectedDrive,
		setSelectedDrive,
		setShowUpdateRoleModal,
	} = useUserContext();
	const { companies, selectedCompany, selectedPositionId } = useRecommendationsContext();

	const [activeCompany, setActiveCompany] = useState<string>('');
	const [activePosition, setActivePosition] = useState<string>();
	const [isaddDriveRoomModalOpen, setIsAddDriveRoomModalOpenModal] = useState<boolean>(false);

	const collegeName = user?.pifMeta?.underGraduate?.college?.name ?? user?.pifMeta?.postGraduate?.college?.name ?? '';

	const [collegeOptionSelected, setCollegeOptionSelected] = useState<{ value: string; label: string } | undefined>({
		label: collegeName,
		value: collegeName,
	});

	const [isUpdateAvailabilityModalOpen, setIsUpdateAvailabilityModalOpen] = useState<boolean>(false);
	const [isUpdatePhoneNumber, setIsUpdatePhoneNumber] = useState<boolean>(false);

	const totalApplicationsCount = useGetMyOpenApplicationsCount(isJobseekerLoggedIn);
	const { data: myRegisteredDrives } = useGetMyRegisteredDrives({ enabled: isJobseekerLoggedIn });
	const signoutFn = useMutation(['signout'], signout, {
		onSuccess() {
			if (isRecruiterSide && isUserRecruiter(user?.role as UserRole)) {
				window.open(
					`${RECRUITER_APP_ENDPOINT}/auth/signin?redirect_url=${encodeURIComponent(window.location.href)}`,
					'_self',
				);
			} else {
				window.open(
					`${STUDENT_APP_ENDPOINT}/auth/signin?redirect_url=${encodeURIComponent(window.location.href)}`,
					'_self',
				);
			}
		},
	});

	const { data: prepPlan } = useGetPrepePlanById({ isJobSeekerLoggedIn: isJobseekerLoggedIn });

	const switchCollegeMutation = useMutation(['switchCollege'], switchCollege, {
		onSuccess(data) {
			toast({
				title: 'College updated successfully',
				type: 'success',
			});
			window.location.reload();
		},
		onError(error: Error) {
			console.log(error);
			toast({
				title: 'Error while switching college.',
				type: 'error',
			});
		},
	});

	const isPaidUser = user?.evaluation?.interviewPath?.type === InterviewPathType.Paid;
	const hasPaidForPrepPlan: boolean =
		prepPlan?.prepPlan?.payment?.status === PaymentStatus.Success &&
		prepPlan?.prepPlan?.payment.type === PaymentType.PrepPlanCompletion &&
		prepPlan?.prepPlan?.payment.amount > 0;

	useEffect(() => {
		if (selectedCompany?.company && selectedCompany?.company?.positions) {
			setActiveCompany(selectedCompany?.company?._id);
			setActivePosition(selectedPositionId);
		} else if (!selectedCompany?.company) {
			const openComp = companies.find(c => c.activePositions > 1);
			setActiveCompany(openComp?.company._id);
			setActivePosition(
				selectedPositionId || (openComp?.company.positions ? openComp?.company.positions[0]?._id ?? '' : ''),
			);
		}
	}, [selectedCompany?.company, selectedPositionId, companies]);

	if (!user) return null;
	return (
		<div
			className="nav-mobile-top transition-all bg-white dark:!bg-[#18222D] w-full h-[70px] font-inter fixed left-0 flex items-center xl:px-[80px] px-[4px] py-[10px] md:py-[18px] right-0 top-0 z-[41] "
			style={{ boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)' }}
		>
			<FlowbiteNavbar className="w-full bg-white dark:!bg-[#18222D]" fluid={true} rounded={true}>
				<div className="flex flex-row justify-between w-full">
					<div className="flex gap-4 items-center">
						{/* Logo */}
						<div className="flex items-center justify-center cursor-pointer">
							<Img
								src={
									themeMode === 'dark'
										? 'https://masai-website-images.s3.ap-south-1.amazonaws.com/Levelup-Logo.png'
										: 'https://masai-website-images.s3.ap-south-1.amazonaws.com/Levelup-Logo.png'
								}
								width="110"
								height="32px"
								alt="LevelUp Logo"
								className="md:block hidden "
								onClick={() => {
									if (isRecruiterLoggedIn) {
										sendTrackingEvent({
											event: 'recr_topnav_levelup_logo',
											eventLabel: location.pathname,
										});
									} else {
										sendTrackingEvent({
											event: 'topnav_logo',
											eventLabel: location.pathname,
										});
									}
									if (isUserJobseeker(user.role)) {
										// navigate('/dashboard');
										navigate('/my-applications');
									} else if (isMentorLoggedIn) {
										navigate('/mentors/interviews');
									} else if (isRecruiterLoggedIn) {
										navigate('/select-hiring-type');
									} else {
										navigate('/');
									}
								}}
							/>

							{/* Mobile Logo */}
							<Img
								src={`${
									themeMode === 'dark'
										? 'https://masai-website-images.s3.ap-south-1.amazonaws.com/Levelup-Logo.png'
										: 'https://masai-website-images.s3.ap-south-1.amazonaws.com/Levelup-Logo.png'
								}`}
								alt="LevelUp Logo"
								className="md:hidden flex w-full max-h-[40px]"
								onClick={() => {
									sendTrackingEvent({
										event: 'topnav_logo',
										eventLabel: location.pathname,
									});
									if (isUserJobseeker(user.role)) {
										// navigate('/dashboard');
										navigate('/my-applications');
									} else if (isUserMentor(user.role)) {
										navigate('/mentors/interviews');
									} else if (isRecruiterLoggedIn) {
										navigate('/select-hiring-type');
									} else {
										navigate('/');
									}
								}}
							/>
						</div>

						{/* Interview and Jobs */}
						{isJobseekerLoggedIn ? (
							<FlowbiteNavbar.Collapse className="md:block hidden ml-[32px]">
								{/* <Link
									onClick={() => {
										sendTrackingEvent({
											event: 'topnav_jobs',
											eventLabel: location.pathname,
										});
									}}
									to="/jobs"
								>
									<p
										className={classNames(
											location.pathname === '/jobs' ? 'text-[#7777E7]' : 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Jobs
									</p>
								</Link> */}

								<Link
									onClick={() => {
										sendTrackingEvent({
											event: 'topnav_applications',
											eventLabel: location.pathname,
										});
									}}
									to="/my-applications"
								>
									<p
										className={classNames(
											location.pathname === '/my-applications'
												? 'text-[#7777E7]'
												: 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Jobs
									</p>
								</Link>

								<Link
									onClick={() => {
										sendTrackingEvent({
											event: 'topnav_user_dashboard',
											eventLabel: location.pathname,
										});
									}}
									to="/user-dashboard"
								>
									<p
										className={classNames(
											location.pathname === '/user-dashboard' ? 'text-[#7777E7]' : 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Dashboard
									</p>
								</Link>

								<Link
									onClick={() => {
										sendTrackingEvent({
											event: 'topnav_support',
											eventLabel: location.pathname,
										});
									}}
									to="/support"
								>
									<p
										className={classNames(
											location.pathname === '/support' ? 'text-[#7777E7]' : 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Support
									</p>
								</Link>

								{myRegisteredDrives?.drives?.length ? (
									<Link
										onClick={() => {
											sendTrackingEvent({
												event: 'topnav_drive',
												eventLabel: location.pathname,
											});
										}}
										to="/offline-drive-day-opportunities"
									>
										<p
											className={classNames(
												location.pathname === '/offline-drive-day-opportunities'
													? 'text-[#7777E7]'
													: 'text-[#1C1C28] dark:text-[#FFFFFF]',
											)}
										>
											Drive
										</p>
									</Link>
								) : null}

								{/* <Link
									onClick={() => {
										sendTrackingEvent({
											event: 'topnav_technical_rounds',
											eventLabel: location.pathname,
										});
									}}
									to="/interviews"
								>
									<p
										className={classNames(
											location.pathname === '/interviews' ? 'text-[#7777E7]' : 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Interviews
									</p>
								</Link> */}
								{/* <Link
									onClick={() => {
										sendTrackingEvent({
											event: 'topnav_technical_rounds',
											eventLabel: location.pathname,
										});
									}}
									to="/assessments"
								>
									<p
										className={classNames(
											location.pathname === '/assessments' ? 'text-[#7777E7]' : 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Assessments
									</p>
								</Link>

								<Link
									onClick={() => {
										sendTrackingEvent({
											event: 'topnav_contests',
											eventLabel: location.pathname,
										});
									}}
									to="/contests"
								>
									<p
										className={classNames(
											location.pathname === '/contests' ? 'text-[#7777E7]' : 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Contests
									</p>
								</Link>

								<Link
									onClick={() => {
										sendTrackingEvent({
											event: 'topnav_challenges',
											eventLabel: location.pathname,
										});
									}}
									to="/challenges"
								>
									<p
										className={classNames(
											location.pathname === '/challenges' ? 'text-[#7777E7]' : 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Challenges
									</p>
								</Link> */}

								{!isPaidUser && hasPaidForPrepPlan && (
									<Link
										onClick={() => {
											sendTrackingEvent({
												event: 'topnav_prepare',
												eventLabel: location.pathname,
											});
										}}
										to="/prepare"
									>
										<p
											className={classNames(
												location.pathname === '/prepare' ? 'text-[#7777E7]' : 'text-[#1C1C28] dark:text-[#FFFFFF]',
											)}
										>
											Prepare
										</p>
									</Link>
								)}
							</FlowbiteNavbar.Collapse>
						) : null}

						{isMentorLoggedIn ? (
							<FlowbiteNavbar.Collapse className="md:block hidden ml-[32px]">
								<Link
									onClick={() => {
										//
									}}
									to="/mentors/interviews"
								>
									<p
										className={classNames(
											location.pathname.includes('mentors/interviews')
												? 'text-[#7777E7]'
												: 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Interviews
									</p>
								</Link>

								<Link
									onClick={() => {
										//
									}}
									to="/mentors/sessions"
								>
									<p
										className={classNames(
											location.pathname.includes('mentors/sessions')
												? 'text-[#7777E7]'
												: 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Sessions
									</p>
								</Link>
							</FlowbiteNavbar.Collapse>
						) : null}

						{isRecruiterLoggedIn || isAccountManagerLoggedIn ? (
							<FlowbiteNavbar.Collapse className="md:block hidden ml-[32px]">
								{isRecruiterLoggedIn && user.role !== UserRole.SUPER_ACCOUNT_MANAGER ? (
									<Link
										className="hover:cursor-pointer"
										onClick={() => {
											sendTrackingEvent({
												event: 'topnav_home',
												eventLabel: location.pathname,
											});
										}}
										to="/select-hiring-type"
									>
										<p
											className={classNames(
												'text-sm font-inter font-[500] text-center',
												location.pathname === '/select-hiring-type'
													? 'text-[#7777E7] font-[600]'
													: 'text-[#1C1C28] dark:text-[#FFFFFF]',
											)}
										>
											Home
										</p>
									</Link>
								) : null}

								{isAccountManagerLoggedIn ? (
									<Link
										className="hover:cursor-pointer"
										onClick={() => {
											sendTrackingEvent({
												event: 'topnav_companies',
												eventLabel: location.pathname,
											});
										}}
										to={'/companies'}
									>
										<p
											className={classNames(
												'text-sm font-inter font-[500] text-center',
												location.pathname === '/companies'
													? 'text-[#7777E7] font-[600]'
													: 'text-[#1C1C28] dark:text-[#FFFFFF]',
											)}
										>
											Companies
										</p>
									</Link>
								) : null}

								{isRecruiterLoggedIn && user.role !== UserRole.SUPER_ACCOUNT_MANAGER && (
									<Link
										className="hover:cursor-pointer"
										onClick={() => {
											sendTrackingEvent({
												event: 'topnav_home',
												eventLabel: location.pathname,
											});
										}}
										to="/recruiter-dashboard"
									>
										<p
											className={classNames(
												'text-sm font-inter font-[500] text-center',
												location.pathname === '/recruiter-dashboard'
													? 'text-[#7777E7] font-[600]'
													: 'text-[#1C1C28] dark:text-[#FFFFFF]',
											)}
										>
											Dashboard
										</p>
									</Link>
								)}

								{/* <Link
									className="hover:cursor-pointer"
									onClick={() => {
										sendTrackingEvent({
											event: 'topnav_candidates',
											eventLabel: location.pathname,
										});
									}}
									to={`/positions/${isAccountManagerLoggedIn ? `${activeCompany}/` : ''}${activePosition}/candidates`}
								>
									<p
										className={classNames(
											'text-sm font-inter font-[500] text-center',
											location.pathname.includes('candidates')
												? 'text-[#7777E7] font-[600]'
												: 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Candidates
									</p>
								</Link> */}
								{/* <Link
									className="hover:cursor-pointer"
									onClick={() => {
										sendTrackingEvent({
											event: 'topnav_technical_rounds',
											eventLabel: location.pathname,
										});
									}}
									to={`/pipeline/${isAccountManagerLoggedIn ? `${activeCompany}/` : ''}${activePosition}`}
								>
									<p
										className={classNames(
											'text-sm font-inter font-[500] text-center',
											location.pathname.includes('/pipeline')
												? 'text-[#7777E7] font-[600]'
												: 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Pipeline
									</p>
								</Link> */}

								{/* <Link
									className="hover:cursor-pointer"
									onClick={() => {
										sendTrackingEvent({
											event: 'topnav_positions',
											eventLabel: location.pathname,
										});
									}}
									to={`/${isAccountManagerLoggedIn ? `my-positions/${activeCompany}` : 'positions'}`}
								>
									<p
										className={classNames(
											'text-sm font-inter font-[500] text-center',
											(
												isAccountManagerLoggedIn
													? location.pathname.includes('my-positions')
													: location.pathname === '/positions'
											)
												? 'text-[#7777E7] font-[600]'
												: 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Positions
									</p>
								</Link> */}

								{isAccountManagerLoggedIn ? (
									<Link
										className="hover:cursor-pointer"
										onClick={() => {
											sendTrackingEvent({
												event: 'topnav_brief-debriefs',
												eventLabel: location.pathname,
											});
										}}
										to={`/brief-debriefs`}
									>
										<p
											className={classNames(
												'text-sm font-inter font-[500] text-center',
												location.pathname === '/brief-debriefs'
													? 'text-[#7777E7] font-[600]'
													: 'text-[#1C1C28] dark:text-[#FFFFFF]',
											)}
										>
											Brief/Debrief
										</p>
									</Link>
								) : null}
								{/* {isLeadGenLoggedIn ? (
									<Link
										className="hover:cursor-pointer"
										onClick={() => {
											sendTrackingEvent({
												event: 'topnav_brief-debriefs',
												eventLabel: location.pathname,
											});
										}}
										to={`/leads-gen`}
									>
										<p
											className={classNames(
												'text-sm font-inter font-[500] text-center',
												location.pathname === '/leads-gen'
													? 'text-[#7777E7] font-[600]'
													: 'text-[#1C1C28] dark:text-[#FFFFFF]',
											)}
										>
											Leads Gen
										</p>
									</Link>
								) : null} */}
								{isRecruiterLoggedIn || isAccountManagerLoggedIn ? (
									<Link
										className="hover:cursor-pointer"
										onClick={() => {
											sendTrackingEvent({
												event: 'topnav_offline-drive',
												eventLabel: location.pathname,
											});
										}}
										to={isAccountManagerLoggedIn ? `/drive-companies` : `/drive-positions`}
									>
										<p
											className={classNames(
												'text-sm font-inter font-[500] text-center',
												location.pathname === '/drive-positions' || location.pathname === '/drive-companies'
													? 'text-[#7777E7] font-[600]'
													: 'text-[#1C1C28] dark:text-[#FFFFFF]',
											)}
										>
											Offline Drive
										</p>
									</Link>
								) : null}

								{isAccountManagerLoggedIn ? (
									<Link
										className="!hover:cursor-pointer"
										onClick={() => {
											sendTrackingEvent({
												event: 'topnav_tickets',
												eventLabel: location.pathname,
											});
										}}
										to="/tickets"
									>
										<p
											className={classNames(
												'text-sm font-inter font-[500] text-center',
												location.pathname.includes('/tickets')
													? 'text-[#7777E7] font-[600]'
													: 'text-[#1C1C28] dark:text-[#FFFFFF]',
											)}
										>
											Tickets
										</p>
									</Link>
								) : null}
							</FlowbiteNavbar.Collapse>
						) : null}

						{isDriveAdminLoggedIn ? (
							<FlowbiteNavbar.Collapse className="md:block hidden ml-[32px]">
								<Link
									className="hover:cursor-pointer"
									onClick={() => {
										sendTrackingEvent({
											event: 'topnav_offline_candidates',
											eventLabel: location.pathname,
										});
									}}
									to={`/offline-drive-candidates${selectedDrive?.value ? `?driveId=${selectedDrive.value}` : ''}`}
								>
									<p
										className={classNames(
											location.pathname.includes('offline-drive-candidates')
												? 'text-[#7777E7]'
												: 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Offline Drive Candidates
									</p>
								</Link>

								<Link
									className="hover:cursor-pointer"
									onClick={() => {
										sendTrackingEvent({
											event: 'topnav_offline_companies',
											eventLabel: location.pathname,
										});
									}}
									to={`/offline-drive-companies${selectedDrive?.value ? `?driveId=${selectedDrive.value}` : ''}`}
								>
									<p
										className={classNames(
											location.pathname.includes('offline-drive-companies')
												? 'text-[#7777E7]'
												: 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Offline Drive Companies
									</p>
								</Link>
								<Link
									className="hover:cursor-pointer"
									onClick={() => {
										sendTrackingEvent({
											event: 'topnav_offline_companies',
											eventLabel: location.pathname,
										});
									}}
									to={`/update-phone-number`}
								>
									<p
										className={classNames(
											location.pathname.includes('update-phone-number')
												? 'text-[#7777E7]'
												: 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										User Updates
									</p>
								</Link>
								<Link
									className="hover:cursor-pointer"
									onClick={() => {
										sendTrackingEvent({
											event: 'topnav_offline_companies',
											eventLabel: location.pathname,
										});
									}}
									to={`/add-drive-room`}
								>
									<p
										className={classNames(
											location.pathname.includes('add-drive-room')
												? 'text-[#7777E7]'
												: 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Add Drive Room
									</p>
								</Link>
							</FlowbiteNavbar.Collapse>
						) : null}

						{isUserUniversityManagerLoggedIn ? (
							<FlowbiteNavbar.Collapse className="md:block hidden ml-[32px]">
								<Link to="/">
									<p
										className={classNames(
											location.pathname.startsWith('/') && location.pathname.endsWith('/')
												? 'text-[#7777E7]'
												: 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Dashboard
									</p>
								</Link>

								<Link to="/student-reports">
									<p
										className={classNames(
											location.pathname.includes('student-reports')
												? 'text-[#7777E7]'
												: 'text-[#1C1C28] dark:text-[#FFFFFF]',
										)}
									>
										Student Reports
									</p>
								</Link>
							</FlowbiteNavbar.Collapse>
						) : null}
					</div>

					{/* Level, Refer, Streak, Avatar */}
					<div className="flex gap-4 items-center">
						{isJobseekerLoggedIn ? (
							<div className="ml-[32px] flex gap-[10px] md:gap-[24px] items-center flex-row navbar-container">
								{/* Refer and Earn */}
								{/* <Link
									onClick={() => {
										sendTrackingEvent({
											event: 'topnav_refer',
											eventLabel: location.pathname,
										});
									}}
									to="/refer"
									key={'/refer'}
									data-testid={'header-navigation-link-refer'}
									className={classNames('custom-text !cursor-pointer text-white')}
								>
									<span
										className={classNames(
											'flex !cursor-pointer items-center tsm:gap-2 py-[2px] md:py-1 px-[3px] md:px-[10px] rounded-[40px] border-[1px] border-[#E57A00]',
											{
												// 'text-white': !router.pathname.startsWith('/refer'),
											},
										)}
									>
										<Img src="/assets/level-up-coins.svg" alt="coin-logo" width="24" height={'24'} />
										<span
											className={classNames(
												location.pathname === '/refer' ? 'text-[#7777E7]' : 'text-[#1C1C28] dark:text-[#FFFFFF]',
												'text-[14px] hidden tsm:block',
											)}
										>
											{user.evaluation.levelCoins > 0 ? user.evaluation.levelCoins : <>Refer &amp; Earn</>}
										</span>
										<span
											className={classNames(
												location.pathname === '/refer' ? 'text-[#7777E7]' : 'text-[#1C1C28] dark:text-[#FFFFFF]',
												'text-[14px] tsm:hidden block min-w-[15px]',
											)}
										>
											{user.evaluation.levelCoins > 0 ? user.evaluation.levelCoins : ''}
										</span>
									</span>
								</Link> */}

								{/* Streak */}
								{/* <div className="text-[#1C1C28] dark:text-[#FFFFFF] rounded-[16px] flex gap-1 md:gap-[6px] py-[3px] md:py-[5px] px-2 md:px-[14px] items-center dark:bg-[#2f3c4b] select-none dark:border-none border">
									<Tooltip arrow={false} content={'Your Streak'}>
										<Img
											src={'/assets/dayStreakFireIcon.svg'}
											width={20}
											height={20}
											className="min-w-[12px] min-h-[15px]"
										/>
									</Tooltip>
									<p>{user.evaluation?.streak ?? 0}</p>
								</div> */}

								{/* Level Icon */}
								{/* <div className="text-[#1C1C28] dark:text-[#FFFFFF] rounded-[16px] flex gap-1 md:gap-[6px] py-[3px] md:py-[5px] px-2 md:px-[14px] items-center dark:bg-[#2f3c4b] select-none dark:border-none border">
									<Tooltip arrow={false} content={'Your Level'}>
										<Img
											src={'/assets/yellowLevelIcon.svg'}
											width={20}
											height={20}
											className="min-w-[12px] min-h-[15px]"
										/>
									</Tooltip>
									<p>{Math.round(user.evaluation?.level ?? 0)}</p>
								</div> */}

								{/* Watch Video */}
								{/* <div className="items-center justify-center md:flex hidden">
									<div className="items-center justify-center md:flex hidden">
										<Link to="#" className={'custom-text !cursor-pointer text-white'}>
											<div className="take-first-step ml-5 flex items-center justify-center sm:mr-5">
												<BsFillPlayCircleFill
													className="h-[24px] w-[24px] text-[#C7C9D9]"
													cursor={'pointer'}
													onClick={() => {
														sendTrackingEvent({
															event: 'topnav_onboarding_video',
															eventLabel: location.pathname,
														});
														setIsOnbaordingVideoModalOpen(true);
													}}
												/>

												<OnboardingVideoModal
													isOpen={isOnbaordingVideoModalOpen}
													onClose={() => {
														setIsOnbaordingVideoModalOpen(false);
													}}
												/>
											</div>
										</Link>
									</div>
								</div> */}
							</div>
						) : null}

						{isRecruiterLoggedIn ? <TrialPeriod /> : null}

						{isAccountManagerLoggedIn ? (
							<div className="hover:cursor-pointer" onClick={() => navigate('/create-company')}>
								<MdAdd />
							</div>
						) : null}

						{isUserUniversityManagerLoggedIn &&
						(user.allowedRoles?.includes(UserRole.ADMIN) ||
							user.allowedRoles?.includes(UserRole.SUPER_ADMIN) ||
							user?.allowedSwitchColleges?.length > 1) ? (
							<div className="w-full min-w-[250px] max-w-[250px] md:min-w-[300px] md:max-w-[300px]">
								<CollegeSelect
									isClearable={false}
									value={collegeOptionSelected}
									styleType="my-select-plans"
									allowedColleges={user.allowedSwitchColleges?.map(el => el?.college) as string[]}
									onChange={e => {
										if (Array.isArray(e)) {
											return;
										} else {
											setCollegeOptionSelected(e);
											switchCollegeMutation.mutate({ college: e?.value });
										}
									}}
								/>
							</div>
						) : null}

						{isDriveAdminLoggedIn ? (
							<div className="hidden md:block md:min-w-[350px]">
								<OfflineDriveDropdown selectedDrive={selectedDrive} setSelectedDrive={setSelectedDrive} />
							</div>
						) : null}

						{/* Dropdown avatar */}
						<div
							className={`${
								// !isRecruiterSide ? 'hidden' : ''
								window?.location?.pathname === '/select-hiring-type' ? 'hidden' : ''
							} md:block [&_div[data-testid='flowbite-dropdown']]:!border-gray-200 dark:[&_div[data-testid='flowbite-dropdown']]:!border-gray-600 `}
						>
							<Dropdown
								arrowIcon={false}
								inline={true}
								color={'#C7C9D9'}
								className={PROFILE_MENU_CLASS}
								label={
									<Avatar
										onClick={() => {
											sendTrackingEvent({
												event: 'topnav_profile',
												eventLabel: location.pathname,
											});
										}}
										data-testid={'header-avatar-1'}
										className="take-first-step [&_img]:border"
										alt="User Profile"
										// onClick={handleNullCase}
										placeholderInitials={stringAvatar(user.name.trim().toUpperCase() || '')}
										rounded={true}
										img={user.profile.proctoringImageUrl || ''}
									/>
								}
								dismissOnClick
							>
								<Dropdown.Header className={PROFILE_MENU_HEADER_CLASS}>
									<div className="flex">
										<span
											className={classNames(
												isUserJobseeker(user.role) && 'dark:text-white',
												'block max-w-[260px] break-words text-[16px] font-[700] text-[#111E2C] md:max-w-[400px]',
											)}
										>
											{user.name}
										</span>
									</div>
									{checkDisplayUserEmail(user) ? (
										<span className="block truncate text-[13px] font-medium text-[#6B6D85]">{user.email}</span>
									) : null}
								</Dropdown.Header>
								{isUserJobseeker(user.role) ? (
									<Dropdown.Item
										className={PROFILE_MENU_ITEM_CLASS}
										data-testid={'header-navigation-link-profile'}
										onClick={() => {
											sendTrackingEvent({
												event: 'profile_ham_myprofile',
												eventLabel: location.pathname,
											});
											navigate('/profile/edit');
										}}
									>
										<HiOutlineUser className="h-[18px] w-[18px] text-[#8A8BA3]" />
										My Profile
									</Dropdown.Item>
								) : null}
								{/* {isUserJobseeker(user.role) && (
									<Dropdown.Item
										className={classNames(isUserJobseeker(user.role) && 'flex gap-2 dark:!text-[#C7C9D9]')}
										data-testid={'header-navigation-link-applications'}
										onClick={() => {
											sendTrackingEvent({
												event: 'profile_ham_myapplication',
												eventLabel: location.pathname,
											});
											navigate(`/my-applications`);
										}}
									>
										<div className="flex items-center gap-2">
											Jobs
											<span className="navbar-nudge-pulse-effect flex h-[18px] w-[18px] items-center justify-center rounded-full border bg-[#c1d1f1] text-[10px] text-black">
												{totalApplicationsCount.data?.count}
											</span>
										</div>
									</Dropdown.Item>
								)} */}
								{isUserJobseeker(user.role) || isUserRecruiter(user.role) || isAccountManagerLoggedIn ? (
									<>
										{/* <Dropdown.Item
											className="flex gap-2 dark:!text-[#C7C9D9]"
											onClick={() => {
												sendTrackingEvent({
													event: 'profile_ham_myapplication',
													eventLabel: location.pathname,
												});
												window.open(`${WEBSITE_URL}/how-levelup-works`, '_blank');
											}}
										>
											How LevelUp Works <FiExternalLink />
										</Dropdown.Item> */}
										{/* {!isUserRecruiter(user.role) ? (
											<Dropdown.Item
												className="flex gap-2 dark:!text-[#C7C9D9]"
												onClick={() => {
													sendTrackingEvent({
														event: 'profile_ham_interview_guideline',
														eventLabel: location.pathname,
													});
													window.open(`${WEBSITE_URL}/how-levelup-works#interview-guidelines-section`, '_blank');
												}}
											>
												Interview Guidelines <FiExternalLink />
											</Dropdown.Item>
										) : null} */}
										{/* {!isAccountManagerLoggedIn ? (
											<Dropdown.Item
												className="flex gap-2 dark:!text-[#C7C9D9]"
												onClick={() => {
													if (isRecruiterLoggedIn) {
														sendTrackingEvent({
															event: 'recr_topnav_profile_support',
															eventLabel: location.pathname,
														});
													} else {
														sendTrackingEvent({
															event: 'profile_ham_support',
															eventLabel: location.pathname,
														});
													}
													window.open(`${WEBSITE_URL}/support`, '_blank');
												}}
											>
												Support <FiExternalLink />
											</Dropdown.Item>
										) : null} */}
									</>
								) : null}
								{[UserRole.MENTOR, UserRole.ALUMNI_CONNECT].includes(user.role) && (
									<Dropdown.Item
										data-testid={'header-navigation-link-update-availability'}
										onClick={() => {
											setIsUpdateAvailabilityModalOpen(true);
										}}
										className="flex gap-2 dark:!text-[#C7C9D9] "
									>
										Update Availability
									</Dropdown.Item>
								)}
								{user.role === UserRole.MENTOR && (
									<Dropdown.Item
										data-testid={'header-navigation-link-edit-profile'}
										onClick={() => {
											navigate(`/mentor/${user._id}`);
										}}
										className="flex gap-2 dark:!text-[#C7C9D9] "
									>
										Edit Profile
									</Dropdown.Item>
								)}

								{isUserDriveAdmin(user.role) ? (
									<div className={classNames('md:hidden px-2 py-2')}>
										<div>
											<OfflineDriveDropdown selectedDrive={selectedDrive} setSelectedDrive={setSelectedDrive} />
										</div>
									</div>
								) : null}

								{/* The header already draws a divider, so only add one when items sit between it and the account actions. */}
								{isUserJobseeker(user.role) ||
								[UserRole.MENTOR, UserRole.ALUMNI_CONNECT].includes(user.role) ||
								isUserDriveAdmin(user.role) ? (
									<Dropdown.Divider />
								) : null}
								{/* {isUserJobseeker(user.role) && !user?.flags?.isMasaiStudent && (
									<Dropdown.Item
										className={classNames('flex gap-2')}
										data-testid={'header-navigation-link-logout'}
										onClick={() => {
											sendTrackingEvent({
												event: 'profile_ham_delete_account',
												eventLabel: location.pathname,
											});

											onDeleteUserModalOpen();
										}}
									>
										Delete Account
									</Dropdown.Item>
								)} */}
								{user?.allowedRoles?.length && user?.allowedRoles?.length > 1 ? (
									<Dropdown.Item
										className={classNames(
											'flex gap-2 !text-black hover:!bg-gray-100 dark:hover:!bg-[#2c3c50] dark:!text-[#C7C9D9]',
										)}
										data-testid={'header-navigation-link-logout'}
										onClick={() => {
											setShowUpdateRoleModal(true);
										}}
									>
										Switch Role{' '}
									</Dropdown.Item>
								) : null}

								{isRecruiterLoggedIn && user.registeredCompany?._id ? (
									<Dropdown.Item
										className={classNames('flex gap-2 dark:!text-[#C7C9D9]')}
										data-testid={'header-navigation-link-logout'}
										onClick={() => {
											sendTrackingEvent({
												event: 'recr_topnav_profile_edit_company_details',
												eventLabel: location.pathname,
											});

											navigate(`/edit-company/${user.registeredCompany?._id}`);
										}}
									>
										Edit Company Details
									</Dropdown.Item>
								) : null}
								<Dropdown.Item
									className={PROFILE_MENU_DANGER_ITEM_CLASS}
									data-testid={'header-navigation-link-logout'}
									onClick={() => {
										if (isRecruiterLoggedIn) {
											sendTrackingEvent({
												event: 'recr_topnav_profile_logout',
												eventLabel: location.pathname,
											});
										} else {
											sendTrackingEvent({
												event: 'profile_ham_logout',
												eventLabel: location.pathname,
											});
										}

										signoutFn.mutate();
									}}
								>
									<HiOutlineLogout className="h-[18px] w-[18px]" />
									Logout
								</Dropdown.Item>
							</Dropdown>
						</div>
						<ThemeToggleButton />
					</div>
				</div>
			</FlowbiteNavbar>

			<UpdateAvailabilityModal
				mentorId={user._id}
				isOpen={isUpdateAvailabilityModalOpen}
				onClose={() => setIsUpdateAvailabilityModalOpen(false)}
			/>
			<LinkedInModal isOpen={isOpen} onClose={onClose} />
		</div>
	);
};

export default Navbar;
